import { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import StatusChip from "@/components/StatusChip";
import { toast } from "sonner";
import RecordActions from "@/components/RecordActions";

import { Plus, UploadSimple, Palette } from "@phosphor-icons/react";
import ImportExportButtons from "@/components/ImportExportButtons";

const CATS = [["","All"],["apparel","Apparel"],["jewellery","Jewellery"],["bags","Bags"],["footwear","Footwear"],["accessories","Accessories"]];

export default function Designs() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ sku: "", name: "", category: "apparel", design_code: "", collection: "", front_image: "https://images.unsplash.com/photo-1571513800374-df1bbe650e56", sizes: [], colours: [], selling_price_inr: 0, selling_price_usd: 0, hsn_code: "", gst_rate: 12, estimated_cost: 0, description: "" });

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      const { data } = await api.get(`/catalog/products?${params}`);
      setProducts(data);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/catalog/products", { ...form, sizes: (form.sizes || "").toString().split(",").map(s=>s.trim()).filter(Boolean), colours: (form.colours || "").toString().split(",").map(s=>s.trim()).filter(Boolean), selling_price_inr: Number(form.selling_price_inr), selling_price_usd: Number(form.selling_price_usd), estimated_cost: Number(form.estimated_cost), gst_rate: Number(form.gst_rate) });
      toast.success("Design created"); setShowNew(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const publish = async (id) => {
    try { await api.post(`/catalog/products/${id}/publish`); toast.success("Published to website"); load(); }
    catch { toast.error("Publish failed"); }
  };

  return (
    <div data-testid="designs-page" className="page-fade">
      <PageHeader eyebrow="Module" title="Designs & catalogue" description="Product SKU master, design library and one-click publishing to the website." tint="rose" icon={Palette} actions={
        <>
          <ImportExportButtons entity="products" onImported={load}/>
          <button className="btn-primary" onClick={()=>setShowNew(true)} data-testid="new-design-btn"><Plus size={14}/> New design</button>
        </>
      }/>
      <div className="flex gap-2 mb-8 flex-wrap">
        {CATS.map(([v,l]) => (
          <button key={v||"all"} onClick={()=>setCategory(v)} data-testid={`design-cat-${v||"all"}`}
                  className={`pill-toggle ${category===v?"is-active":""}`}>{l}</button>
        ))}
      </div>

      {products.length === 0 ? (
        <div className="card-soft"><EmptyState icon={Palette} title="No designs yet" description="Create your first design SKU to build the catalogue." /></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 stagger">
          {products.map((p) => (
            <div key={p._id} className="card-soft p-0 overflow-hidden group cursor-pointer" onClick={()=>setSelected(p)} data-testid={`design-card-${p.sku}`}>
              <div className="aspect-[3/4] overflow-hidden bg-muted">
                {p.front_image && <img src={p.front_image} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"/>}
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display text-[17px] font-semibold leading-tight tracking-tight">{p.name}</div>
                    <div className="text-[11px] font-mono text-muted-foreground truncate mt-1">{p.sku}</div>
                  </div>
                  <StatusChip value={p.status}/>
                </div>
                <div className="mt-3 tabular text-[15px] font-medium">{inr(p.selling_price_inr)}</div>
                <div className="flex gap-2 mt-5">
                  <button onClick={(e)=>{e.stopPropagation();setSelected(p);}} className="btn-ghost flex-1 btn-sm" data-testid={`design-view-${p.sku}`}>View</button>
                  {!p.published && <button onClick={(e)=>{e.stopPropagation();publish(p._id);}} className="btn-primary flex-1 btn-sm" data-testid={`design-publish-${p.sku}`}><UploadSimple size={12}/> Publish</button>}
                  <RecordActions entity="products" record={p} frozen={["sku"]} onChanged={load} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-40 modal-backdrop flex fade-in" onClick={()=>setSelected(null)}>
          <div className="ml-auto w-full max-w-2xl h-full bg-card border-l border-border overflow-y-auto drawer-slide" onClick={(e)=>e.stopPropagation()} data-testid="design-drawer">
            <div className="aspect-[16/10] bg-muted"><img src={selected.front_image} alt="" className="w-full h-full object-cover"/></div>
            <div className="p-7">
              <div className="label-eyebrow">{selected.design_code || "—"} · {selected.collection || "Unassigned"}</div>
              <div className="display-2 mt-2">{selected.name}</div>
              <div className="flex gap-2 mt-4"><StatusChip value={selected.status}/>{selected.published && <StatusChip value="live"/>}</div>
              <p className="text-sm text-muted-foreground mt-5 leading-relaxed">{selected.description || "No description provided yet."}</p>
              <div className="grid grid-cols-2 gap-6 mt-8 text-sm">
                <div><span className="label-eyebrow">SKU</span><div className="font-mono mt-1.5">{selected.sku}</div></div>
                <div><span className="label-eyebrow">HSN</span><div className="font-mono mt-1.5">{selected.hsn_code || "—"}</div></div>
                <div><span className="label-eyebrow">Price INR</span><div className="tabular mt-1.5">{inr(selected.selling_price_inr)}</div></div>
                <div><span className="label-eyebrow">Price USD</span><div className="tabular mt-1.5">${selected.selling_price_usd}</div></div>
                <div><span className="label-eyebrow">Sizes</span><div className="mt-1.5">{(selected.sizes||[]).join(" · ") || "—"}</div></div>
                <div><span className="label-eyebrow">Colours</span><div className="mt-1.5">{(selected.colours||[]).join(" · ") || "—"}</div></div>
                <div><span className="label-eyebrow">Est. cost</span><div className="tabular mt-1.5">{selected.estimated_cost ? inr(selected.estimated_cost) : "—"}</div></div>
                <div><span className="label-eyebrow">GST</span><div className="tabular mt-1.5">{selected.gst_rate}%</div></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={()=>setShowNew(false)}>
          <form onSubmit={submit} onClick={(e)=>e.stopPropagation()} className="modal-surface modal-scale p-7 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-design-form">
            <div className="label-eyebrow">New design</div>
            <div className="display-3 mt-1 mb-6">Create</div>
            <div className="grid grid-cols-2 gap-5">
              {[["sku","SKU"],["name","Name"],["design_code","Design code"],["collection","Collection"],["hsn_code","HSN"]].map(([k,l])=>(
                <label key={k} className="block"><span className="label-eyebrow">{l}</span>
                  <input required={k==="sku"||k==="name"} value={form[k]||""} onChange={(e)=>setForm({...form,[k]:e.target.value})}
                         className="input-underline" data-testid={`design-input-${k}`}/></label>
              ))}
              <label className="block"><span className="label-eyebrow">Category</span>
                <select value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})}
                        className="input-soft mt-2" data-testid="design-input-category">
                  {["apparel","jewellery","bags","footwear","accessories","sample"].map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="block"><span className="label-eyebrow">Sizes (comma-sep)</span>
                <input value={form.sizes} onChange={(e)=>setForm({...form,sizes:e.target.value})}
                       className="input-underline" data-testid="design-input-sizes"/></label>
              <label className="block"><span className="label-eyebrow">Colours (comma-sep)</span>
                <input value={form.colours} onChange={(e)=>setForm({...form,colours:e.target.value})}
                       className="input-underline" data-testid="design-input-colours"/></label>
              <label className="block"><span className="label-eyebrow">Price INR</span>
                <input type="number" value={form.selling_price_inr} onChange={(e)=>setForm({...form,selling_price_inr:e.target.value})}
                       className="input-underline" data-testid="design-input-price-inr"/></label>
              <label className="block"><span className="label-eyebrow">Price USD</span>
                <input type="number" value={form.selling_price_usd} onChange={(e)=>setForm({...form,selling_price_usd:e.target.value})}
                       className="input-underline" data-testid="design-input-price-usd"/></label>
              <label className="block"><span className="label-eyebrow">Est. cost</span>
                <input type="number" value={form.estimated_cost} onChange={(e)=>setForm({...form,estimated_cost:e.target.value})}
                       className="input-underline"/></label>
              <label className="block"><span className="label-eyebrow">GST %</span>
                <input type="number" value={form.gst_rate} onChange={(e)=>setForm({...form,gst_rate:e.target.value})}
                       className="input-underline"/></label>
            </div>
            <label className="block mt-5"><span className="label-eyebrow">Front image URL</span>
              <input value={form.front_image} onChange={(e)=>setForm({...form,front_image:e.target.value})}
                     className="input-underline font-mono text-xs" data-testid="design-input-image"/></label>
            <label className="block mt-5"><span className="label-eyebrow">Description</span>
              <textarea value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})}
                        className="textarea-soft mt-2"/></label>
            <div className="flex gap-2 mt-8">
              <button type="button" onClick={()=>setShowNew(false)} className="btn-ghost flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="design-submit">Create design</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
